import type { QagentChatDict } from '../en/qagentChat';

export const qagentChat: QagentChatDict = {
  header: {
    newChat: 'Ny chatt',
    resources: 'Resurser',
    exportConversation: 'Exportera till Word',
    exportConversationHint: 'Ladda ner konversationen som ett Word-dokument',
  },
  export: {
    defaultTitle: 'QAgent-konversation',
    dateLine: (date: string) => `Exporterad från Queri den ${date}`,
    youLabel: 'Du',
    failed: 'Det gick inte att skapa Word-dokumentet. Försök igen.',
  },
  greeting: {
    welcome: (firstName: string) => `Välkommen, ${firstName}`,
    hello: 'Hej där',
    prompt: 'Vilka juridiska frågor tar vi oss an idag?',
  },
  messages: {
    loading: 'Laddar meddelanden …',
  },
  input: {
    placeholderWithMentions:
      'Meddela QAgent... (skriv @ för att nämna, / för kommandon)',
    placeholderNoMentions: 'Meddela QAgent... (skriv / för kommandon)',
    sendAriaLabel: 'Skicka meddelande',
    sendTitle: 'Skicka meddelande (Enter)',
    failed: 'Det gick inte att skicka meddelandet',
    escToStopPrefix: 'Tryck',
    escToStopSuffix: 'för att stoppa genereringen',
    escKey: 'Esc',
  },
  projectIndicator: {
    fileCount: (count: number) => `${count} fil${count !== 1 ? 'er' : ''}`,
    clearTitle: 'Rensa meddelanden och aktiv kontext',
    clear: 'Rensa',
    emailAttachments: 'E-postbilagor',
  },
  picker: {
    selectFilePrompt: 'Välj en fil ovan …',
    fileChipFallback: 'Fil',
    titles: {
      review: 'Granska ett dokument',
      compare: 'Jämför två dokument',
      summarize: 'Sammanfatta ett dokument',
      'redline-summary': 'Sammanfatta spårade ändringar',
      default: 'Välj ett dokument',
    },
    filesSelectTwo: 'Filer (välj 2)',
    standardAgreements: 'Våra standardavtal',
    file: 'Fil',
    noFilesAvailable: 'Inga filer tillgängliga',
    playbook: 'Playbook',
    optional: '(valfritt)',
    startComparison: 'Starta jämförelse',
    compareWithStandard: 'Jämför med standardavtal',
    selectFiles: (remaining: number) =>
      `Välj ${remaining} fil${remaining === 1 ? '' : 'er'}`,
    startReview: 'Starta granskning',
    startSummary: 'Starta sammanfattning',
    startAnalysis: 'Starta analys',
    selectFileToContinue: 'Välj en fil för att fortsätta',
  },
  suggestions: {
    heading: 'AI-förslag',
    pendingCount: (count: number) => `${count} väntande`,
    rejectAll: 'Avvisa alla',
    acceptAll: 'Godkänn alla',
  },
  dialogs: {
    upload: {
      title: 'Laddar upp fil …',
      progress: (percent: number) => `${percent}% klart`,
    },
    linkPlaybook: {
      title: 'Länka playbook',
      description:
        'Välj en playbook att länka till detta projekt. Länkade playbooks kan användas för att vägleda AI-svar.',
      label: 'Playbook',
      placeholder: 'Välj en playbook',
      noneAvailable: 'Inga tillgängliga playbooks',
      linking: 'Länkar …',
      submit: 'Länka playbook',
    },
    deleteFile: {
      title: 'Radera fil',
      description: (name: string) =>
        `Är du säker på att du vill radera "${name}"? Denna åtgärd kan inte ångras.`,
      deleting: 'Raderar …',
    },
    unlinkPlaybook: {
      title: 'Avlänka playbook',
      description: (name: string) =>
        `Är du säker på att du vill avlänka "${name}" från detta projekt?`,
      unlinking: 'Avlänkar …',
      submit: 'Avlänka',
    },
  },
  dropZone: {
    withProject: (projectName: string) =>
      `Släpp för att bifoga till ${projectName}`,
    noProject: 'Släpp för att bifoga till detta projekt',
  },
  toasts: {
    standardAgreementFailed: 'Det gick inte att lägga till standardavtalet i projektet',
    loadChatFailed: 'Det gick inte att ladda chatten',
    exportingContract: 'Exporterar avtal …',
    exported: (fileName: string) => `Exporterat: ${fileName}`,
    exportContractFailed: 'Det gick inte att exportera avtalet',
    noFileToExport: 'Ingen fil tillgänglig att exportera',
    exportingDocument: 'Exporterar dokument …',
    exportFileFailed: 'Det gick inte att exportera filen',
    exportedWithSkipped: (applied: number, skipped: number) =>
      `${applied} ändringar tillämpades, ${skipped} kunde inte tillämpas eftersom deras ursprungliga text inte hittades i dokumentet. De utelämnades, så granska dokumentet.`,
    exportNothingApplied: (skipped: number) =>
      `Inga ändringar tillämpades: den ursprungliga texten för ${skipped} förslag hittades inte i dokumentet.`,
    requiresProject: 'Detta kommando kräver att ett projekt är bifogat',
    requiresFiles: 'Detta kommando kräver filer i projektet',
    enterDocType: 'Ange en dokumenttyp',
    initContractFailed: 'Det gick inte att initiera avtalsgenereringen',
    startContractFailed: 'Det gick inte att starta avtalsgenereringen',
    contractStateNotFound: 'Avtalstillstånd hittades inte',
    processDetailsFailed: 'Det gick inte att behandla avtalsuppgifterna',
    noContractId: 'Inget avtals-ID returnerades',
    processingDetails: 'Behandlar uppgifter',
    startSectionFailed: 'Det gick inte att starta sektionsgenereringen',
    generating: 'genererar',
    contractGenerationFailed: 'Avtalsgenereringen misslyckades',
    projectAttached: 'Projekt bifogat',
    projectDetached: 'Projekt frånkopplat',
    updateProjectFailed: 'Det gick inte att uppdatera projektet',
    fileUploaded: (name: string) => `Filen "${name}" laddades upp`,
    uploadFileFailed: 'Det gick inte att ladda upp filen',
    uploadFileError: 'Fel vid uppladdning av fil',
    playbookLinked: 'Playbook länkad',
    linkPlaybookFailed: 'Det gick inte att länka playbook',
    linkPlaybookError: 'Fel vid länkning av playbook',
    fileDeleted: (name: string) => `Filen "${name}" raderad`,
    deleteFileFailed: 'Det gick inte att radera filen',
    deleteFileError: 'Fel vid radering av fil',
    playbookUnlinked: (name: string) => `Playbook "${name}" avlänkad`,
    unlinkPlaybookFailed: 'Det gick inte att avlänka playbook',
    unlinkPlaybookError: 'Fel vid avlänkning av playbook',
    suggestionAccepted: 'Förslag godkänt',
    suggestionApplyFailed: (count: number) =>
      count === 1
        ? 'En godkänd ändring kunde inte placeras i dokumentet automatiskt – granska den i dokumentvyn.'
        : `${count} godkända ändringar kunde inte placeras i dokumentet automatiskt – granska dem i dokumentvyn.`,
    acceptSuggestionFailed: 'Det gick inte att godkänna förslaget',
    acceptSuggestionError: 'Fel vid godkännande av förslag',
    suggestionRejected: 'Förslag avvisat',
    rejectSuggestionFailed: 'Det gick inte att avvisa förslaget',
    rejectSuggestionError: 'Fel vid avvisning av förslag',
    suggestionUndone: 'Förslag ångrat',
    undoSuggestionFailed: 'Det gick inte att ångra förslaget',
    undoSuggestionError: 'Fel vid ångring av förslag',
    suggestionsAccepted: (count: number) => `${count} förslag godkända`,
    acceptSuggestionsFailed: 'Det gick inte att godkänna förslagen',
    acceptSuggestionsError: 'Fel vid godkännande av förslag',
    suggestionsRejected: (count: number) => `${count} förslag avvisade`,
    rejectSuggestionsFailed: 'Det gick inte att avvisa förslagen',
    rejectSuggestionsError: 'Fel vid avvisning av förslag',
    fileNotFound: 'Filen hittades inte',
    preparingDocument: 'Förbereder dokument …',
    downloaded: (fileName: string) => `Nedladdat: ${fileName}`,
    downloadDocumentFailed: 'Det gick inte att ladda ner dokumentet',
    regenerateFailed: 'Det gick inte att regenerera',
    sendMessageFailed: 'Det gick inte att skicka meddelandet',
    contractSaved: (fileName: string, projectName: string) =>
      `${fileName} sparades i ${projectName}`,
    contractSavedNewProject: (fileName: string, projectName: string) =>
      `Skapade projektet "${projectName}" och sparade ${fileName} där`,
    saveContractFailed: 'Kunde inte spara avtalet i projektfiler',
  },
  contractForm: {
    title: 'Dokumentuppgifter',
    required: (filled: number, total: number) => `${filled}/${total} obligatoriska`,
    parties: 'Parter',
    additionalDetails: 'Ytterligare uppgifter',
    optional: 'valfritt',
    generate: 'Skapa dokument',
    moreDetails: (count: number) => `Fler uppgifter (${count})`,
    fewerDetails: 'Färre uppgifter',
    submitted: 'Uppgifter som användes för dokumentet',
  },
  projectChat: {
    loadFailed: 'Det gick inte att läsa in projektchatten',
    unexpectedError: 'Ett oväntat fel uppstod',
    goHome: 'Tillbaka till startsidan',
    loading: 'Läser in projekt …',
  },
  mentionMenu: {
    title: '@ Nämn',
    playbooks: 'Playbooks',
    files: 'Filer',
    knowledge: 'Kunskap',
    noPlaybooks: 'Inga playbooks',
    noFiles: 'Inga filer',
    soon: 'Snart',
    unavailable: 'Inte tillgängligt',
    noItems: 'Inga objekt tillgängliga',
    comingSoon: 'Kommer snart',
    knowledgeHint: 'Sök i alla dina dokument',
  },
  slashCommands: {
    comingSoon: 'Kommer snart',
    requiresProject: 'Kräver ett projekt',
    requiresFiles: 'Kräver filer',
    commands: {
      review: { name: 'Granska', description: 'Juridisk granskning av ett dokument' },
      compare: { name: 'Jämför', description: 'Jämför två dokument sida vid sida' },
      search: { name: 'Sök', description: 'Sök i dokument och på webben' },
      summarize: { name: 'Sammanfatta', description: 'Sammanfattning av ett dokument' },
      'redline-summary': {
        name: 'Sammanfatta ändringar',
        description: 'Sammanfatta spårade ändringar och kommentarer',
      },
      generate: { name: 'Skapa', description: 'Skapa ett nytt juridiskt dokument' },
      export: { name: 'Exportera', description: 'Exportera dokumentet med godkända ändringar' },
      clear: { name: 'Rensa', description: 'Rensa konversationshistoriken' },
    },
  },
  contract: {
    writingProgress: (written: number, total: number) => `${written} av ${total} avsnitt klara`,
    writingSection: 'Skriver …',
    generatePrefix: (docType: string) => `Generera ett ${docType}`,
    generateWithDescription: (docType: string, description: string) =>
      `Generera ett ${docType}: ${description}`,
    party1: 'dokument 1',
    party2: 'dokument 2',
    compare: (file1: string, file2: string) =>
      `Jämför @[${file1}] och @[${file2}]`,
    compareWithStandard: (file: string, standardFile: string, standardName: string) =>
      `Jämför @[${file}] med vårt standardavtal @[${standardFile}] ("${standardName}"). Redovisa avvikelserna från vår standard, risken för oss och föreslagna positioner.`,
    documentFallback: 'dokument',
    reviewWithPlaybook: (fileName: string, pbName: string) =>
      `Granska @[${fileName}] med playbooken @[${pbName}]`,
    reviewThorough: (fileName: string) =>
      `Utför en grundlig juridisk granskning av @[${fileName}]`,
    summarize: (fileName: string) => `Sammanfatta @[${fileName}]`,
    redlineSummary: (fileName: string) =>
      `Sammanfatta de spårade ändringarna och kommentarerna i @[${fileName}]`,
    analyze: (fileName: string) => `Analysera @[${fileName}]`,
    playbookFallback: 'Playbook',
    fileFallback: 'Fil',
    generationFailedTitle: 'Avtalet kunde inte slutföras',
    generationFailedBody: 'Något gick fel när det skrevs, oftast ett tillfälligt problem hos oss. Försök igen för att generera det från uppgifterna du angav.',
    generationRetry: 'Försök igen',
    ready: (docType: string) => `${docType} klart`,
    sectionsGenerated: (count: number) =>
      `${count} avsnitt ${count === 1 ? 'genererat' : 'genererade'}`,
    savingToProject: 'Sparar till projektfiler …',
    savedToProject: (projectName: string) => `Sparat i ${projectName}`,
    savedToProjectFiles: 'Sparat i dina projektfiler',
    saveFailed: 'Kunde inte spara till projektfiler',
    notSaved: 'Inte sparat i projektfiler',
    saveToProject: 'Spara i projekt',
    retrySave: 'Försök igen',
    openDocument: 'Öppna dokument',
    showSections: 'Visa avsnitt',
    hideSections: 'Dölj avsnitt',
    rulesApplied: 'Playbook-regler tillämpade',
    rulesAppliedDescription: 'Dessa regler från din playbook påverkade avtalet:',
    ruleConfidence: (confidence: string) => `Tillämpad med ${confidence === 'high' ? 'hög' : confidence === 'medium' ? 'medel' : 'låg'} säkerhet`,
    showInDocument: 'Klicka för att visa i dokumentet',
  },
};
